import { CSRF_COOKIE, CSRF_HEADER } from '@fos/shared/contracts/identity';
import { type AppErrorCode, DEFAULT_ERROR_MESSAGES, isAppErrorCode } from '@fos/shared/errors';
import { formatWait } from './format';

/**
 * Klient REST dla /api/* (logowanie, sesja, pliki, eksporty). Ciasteczka same-origin (proxy Vite → API),
 * mutacje z nagłówkiem CSRF (double submit: wartość ciasteczka `fos_csrf`), docs/API_AUTH.md.
 */

export class ApiError extends Error {
  readonly code: AppErrorCode;
  readonly status: number;
  readonly details?: unknown;
  /** Sekundy do ponowienia (429), z nagłówka Retry-After albo `details`. */
  readonly retryAfterSec?: number;

  constructor(init: {
    code: AppErrorCode;
    status: number;
    message?: string;
    details?: unknown;
    retryAfterSec?: number;
  }) {
    super(init.message || DEFAULT_ERROR_MESSAGES[init.code]);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    if (init.details !== undefined) this.details = init.details;
    if (init.retryAfterSec !== undefined) this.retryAfterSec = init.retryAfterSec;
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/** Błąd sieci (API niedostępne, brak internetu). */
export class NetworkError extends Error {
  constructor() {
    super('Brak połączenia z serwerem. Sprawdź internet i spróbuj ponownie.');
    this.name = 'NetworkError';
  }
}

export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  for (const part of document.cookie.split(';')) {
    const [key, ...rest] = part.trim().split('=');
    if (key === name) return decodeURIComponent(rest.join('='));
  }
  return null;
}

export function csrfHeaders(): Record<string, string> {
  const token = readCookie(CSRF_COOKIE);
  return token ? { [CSRF_HEADER]: token } : {};
}

const CODE_BY_STATUS: Record<number, AppErrorCode> = {
  400: 'VALIDATION_ERROR',
  401: 'UNAUTHENTICATED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  412: 'APPROVAL_REQUIRED',
  429: 'RATE_LIMITED',
  503: 'INTEGRATION_UNAVAILABLE',
};

function parseRetryAfter(header: string | null, details: unknown): number | undefined {
  if (details && typeof details === 'object') {
    const d = details as Record<string, unknown>;
    for (const key of ['retryAfterSec', 'retryAfter', 'retryAfterSeconds']) {
      if (typeof d[key] === 'number' && Number.isFinite(d[key])) return d[key] as number;
    }
  }
  if (!header) return undefined;
  const seconds = Number(header);
  if (Number.isFinite(seconds)) return seconds;
  const date = Date.parse(header);
  return Number.isNaN(date) ? undefined : Math.max(0, Math.round((date - Date.now()) / 1000));
}

export interface ApiRequest {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

/** Wymienny transport (tryb dev ?mock=1 podmienia go na atrapę, patrz lib/mock). */
export type ApiTransport = (path: string, init: ApiRequest) => Promise<unknown>;

let transport: ApiTransport | null = null;

export function setApiTransport(next: ApiTransport | null): void {
  transport = next;
}

const SESSION_PATH = '/api/auth/session';

/** Zapewnia ciasteczko CSRF przed pierwszą mutacją (GET /api/auth/session zawsze je ustawia). */
async function ensureCsrf(): Promise<void> {
  if (readCookie(CSRF_COOKIE)) return;
  try {
    await fetch(SESSION_PATH, { credentials: 'same-origin', headers: { accept: 'application/json' } });
  } catch {
    // brak sieci obsłuży właściwe żądanie
  }
}

export async function apiFetch<T>(path: string, init: ApiRequest = {}): Promise<T> {
  if (transport) return (await transport(path, init)) as T;
  const method = init.method ?? 'GET';
  const mutation = method !== 'GET';
  if (mutation) await ensureCsrf();

  const headers: Record<string, string> = { accept: 'application/json' };
  if (init.body !== undefined) headers['content-type'] = 'application/json';
  if (mutation) Object.assign(headers, csrfHeaders());

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      credentials: 'same-origin',
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
      signal: init.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new NetworkError();
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }

  if (!response.ok) {
    const body = (data as { error?: { code?: unknown; message?: unknown; details?: unknown } } | null)?.error;
    const code: AppErrorCode = isAppErrorCode(body?.code)
      ? body.code
      : (CODE_BY_STATUS[response.status] ?? (response.status >= 500 ? 'INTERNAL' : 'VALIDATION_ERROR'));
    // Brama/proxy bez odpowiedzi API (np. Vite, gdy API nie działa): 5xx bez treści JSON = brak połączenia.
    if (response.status === 502 || response.status === 504 || (response.status >= 500 && !body)) {
      throw new NetworkError();
    }
    throw new ApiError({
      code,
      status: response.status,
      message: typeof body?.message === 'string' ? body.message : undefined,
      details: body?.details,
      retryAfterSec: parseRetryAfter(response.headers.get('retry-after'), body?.details),
    });
  }
  return data as T;
}

/**
 * Komunikat dla użytkownika. Dla 429 z czytelnym czasem: „Zbyt wiele prób. Spróbuj ponownie za 15 min.”
 */
export function errorMessage(err: unknown, fallback = DEFAULT_ERROR_MESSAGES.INTERNAL): string {
  if (err instanceof ApiError) {
    if (err.code === 'RATE_LIMITED' && err.retryAfterSec !== undefined) {
      return `Zbyt wiele prób. Spróbuj ponownie za ${formatWait(err.retryAfterSec)}.`;
    }
    return err.message;
  }
  if (err instanceof NetworkError) return err.message;
  if (err && typeof err === 'object' && 'message' in err && typeof err.message === 'string' && err.message) {
    return err.message;
  }
  return fallback;
}

/** Kod błędu aplikacji z ApiError albo błędu tRPC (`error.data.appCode`). */
export function appErrorCode(err: unknown): AppErrorCode | null {
  if (err instanceof ApiError) return err.code;
  if (err && typeof err === 'object') {
    const data = (err as { data?: { appCode?: unknown } }).data;
    if (isAppErrorCode(data?.appCode)) return data.appCode;
    const shape = (err as { shape?: { data?: { appCode?: unknown } } }).shape;
    if (isAppErrorCode(shape?.data?.appCode)) return shape.data.appCode;
  }
  return null;
}

/** Problemy walidacji z serwera: `details.issues: [{ path, message }]`. */
export function validationIssues(err: unknown): Array<{ path: string; message: string }> {
  const details =
    err instanceof ApiError ? err.details : (err as { data?: { details?: unknown } } | null)?.data?.details;
  const issues = (details as { issues?: unknown } | undefined)?.issues;
  if (!Array.isArray(issues)) return [];
  return issues.filter(
    (i): i is { path: string; message: string } =>
      typeof i === 'object' && i !== null && typeof i.path === 'string' && typeof i.message === 'string',
  );
}
